import React, { useState } from "react"
import moment from "moment"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import Pagination from "~/library/Pagination"
import ButtonGroup from "~/library/ButtonGroup"
import NavigationLink from "~/library/NavigationLink"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { capitalize, formatCurrencyCents } from "~/utils/helpers"
import { apiBookings } from "~/routes"
import type Booking from "~/types/Booking"

const BookingList = () => {
  const { venueSlug } = getAppConfig()
  const [bookingScope, setBookingScope] = useState("upcoming")

  const {
    data: bookings,
    refetch,
    metadata,
    isLoading,
  } = useFetchResource<{ bookings: Booking[] }, "bookings">(
    apiBookings.index.path({ venueSlug }),
    "bookings",
    { page: "1", scope: bookingScope }
  )

  const handlePageChange = (page: string) => {
    refetch({ page: page, scope: bookingScope })
  }

  const formatStatus = (status: string) => {
    if (status === "pending") {
      return <span className="badge badge-lg bg-warning">Pending</span>
    } else if (status === "confirmed") {
      return <span className="badge badge-lg bg-success">Confirmed</span>
    } else {
      return capitalize(status)
    }
  }

  const handleChangeBookingScope = (value: string) => {
    setBookingScope(value)
    refetch({ page: "1", scope: value })
  }

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle title="Bookings" newItemPath="/bookings/new" />

      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-middle mb-3">
            <ButtonGroup
              options={[
                { label: "Upcoming", value: "upcoming" },
                { label: "Historical", value: "historical" },
                { label: "Archived", value: "archived" },
              ]}
              onClick={handleChangeBookingScope}
              selectedValue={bookingScope}
            />
          </div>
          <CatchCard className={"card-dark-theme"}>
            <CatchTable
              data={bookings}
              emptyMessage="No Bookings"
              columns={[
                {
                  key: "name",
                  header: "Booking",
                  formatter: (name, data) => (
                    <NavigationLink type="bookings" to={`/bookings/${data.id}`}>
                      {name}
                    </NavigationLink>
                  ),
                },
                {
                  key: "date",
                  header: "Date",
                  formatter: (date) => moment(date).format("MMM Do YYYY"),
                },
                {
                  key: "area",
                  header: "Area",
                  formatter: (area) => (
                    <NavigationLink type="areas" to={`/areas/${area.id}`}>
                      {area.name}
                    </NavigationLink>
                  ),
                },
                {
                  key: "client",
                  header: "Client",
                  formatter: (client) => client?.name,
                },
                // {
                //   key: "payments",
                //   header: "Payments",
                // },
                {
                  key: "anticipatedRevenueCents",
                  header: "Anticipated Revenue",
                  formatter: (antRev) => formatCurrencyCents(antRev),
                },
                {
                  key: "status",
                  header: "Status",
                  formatter: (status) => formatStatus(status),
                },
              ]}
            />
            {metadata && (
              <Pagination
                metadata={metadata}
                onPageChange={(page: number) => handlePageChange(page.toString())}
              />
            )}
          </CatchCard>
        </div>
      </div>
    </LoadingWrapper>
  )
}

export default BookingList
